import { useEffect, useRef } from 'react';
import { TRAIL_POOL_SIZE, TRAIL_MIN_PX } from './fxConstants';
import { TRAIL_GLYPHS } from './trailGlyphs';

/**
 * Global pointermove listener that drops short-lived sparkle glyphs
 * along the cursor path. Object-pooled to avoid GC churn; spawns only
 * after the cursor has travelled TRAIL_MIN_PX since the last spark.
 */
export default function CursorTrailLayer() {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const glyphs = TRAIL_GLYPHS;

    const pool: HTMLSpanElement[] = [];
    for (let i = 0; i < TRAIL_POOL_SIZE; i += 1) {
      const span = document.createElement('span');
      span.className = 'fx-trail-spark';
      span.setAttribute('aria-hidden', 'true');
      root.appendChild(span);
      pool.push(span);
    }

    const free = new Set(pool);
    let lastX: number | null = null;
    let lastY: number | null = null;
    let pendingX = 0;
    let pendingY = 0;
    let scheduled = false;
    let rafId = 0;

    const acquire = (): HTMLSpanElement | null => {
      const it = free.values().next();
      if (it.done) return null;
      free.delete(it.value);
      return it.value;
    };

    const release = (span: HTMLSpanElement) => {
      span.classList.remove('trail-pop');
      span.textContent = '';
      free.add(span);
    };

    const flush = () => {
      scheduled = false;
      const x = pendingX;
      const y = pendingY;
      if (lastX === null || lastY === null) {
        lastX = x; lastY = y;
        return;
      }
      const dx = x - lastX;
      const dy = y - lastY;
      if (dx * dx + dy * dy < TRAIL_MIN_PX * TRAIL_MIN_PX) return;
      lastX = x; lastY = y;

      const span = acquire();
      if (!span) return;
      const jitterX = (Math.random() * 2 - 1) * 8;
      const jitterY = (Math.random() * 2 - 1) * 6;
      const drift = (Math.random() * 2 - 1) * 18;
      const rot = (Math.random() * 2 - 1) * 40;
      const scale = 0.7 + Math.random() * 0.6;
      span.style.left = `${x + jitterX}px`;
      span.style.top = `${y + jitterY}px`;
      span.style.setProperty('--dx', `${drift}px`);
      span.style.setProperty('--r', `${rot}deg`);
      span.style.setProperty('--s', `${scale}`);
      span.textContent = glyphs[Math.floor(Math.random() * glyphs.length)];
      void span.offsetWidth;
      span.classList.add('trail-pop');
      const onEnd = () => { span.removeEventListener('animationend', onEnd); release(span); };
      span.addEventListener('animationend', onEnd);
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
      pendingX = e.clientX;
      pendingY = e.clientY;
      if (!scheduled) {
        scheduled = true;
        rafId = requestAnimationFrame(flush);
      }
    };

    document.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('pointermove', onMove);
      pool.forEach((s) => s.remove());
    };
  }, []);

  return <div ref={rootRef} className="fx-trail-root" aria-hidden="true" />;
}
